import { ThemeProvider } from '@emotion/react';
import BookmarkBorderOutlined from '@mui/icons-material/BookmarkBorderOutlined';
import Grid3x3 from '@mui/icons-material/Grid3x3';
import LaunchOutlined from '@mui/icons-material/LaunchOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import MailOutline from '@mui/icons-material/MailOutline';
import PersonOutline from '@mui/icons-material/PersonOutline';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import { createTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { makeStyles, withStyles } from '@mui/styles';
import React from 'react';
import './style.css';
import { inputBaseClasses } from '@mui/material';

const useStyles = makeStyles(theme => ({
  inputText: {
    margin: 'unset',
  },
}));
const colortheme = createTheme({
  palette: {
    error: {
      main: '#c1c1c1',
    },
    primary: {
      main: '#292739',
    },
    secondary: {
      main: '#c1c1c1',
    },
  },
  typography: {
    fontFamily: ['lato'].join(','),
  },
});

const WhiteTextField = withStyles({
  root: {
    '& .MuiInputBase-input': {
      color: '#c1c1c1',
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: '#fff8',
      bottom: '-5px',
    },
    '& .MuiInput-underline:hover:before': {
      borderBottomColor: '#fff',
      bottom: '-5px',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#fff',
      bottom: '-5px',
    },
    [`& .${inputBaseClasses.disabled}`]: {
      color: '#fff8 !important',
      "-webkit-text-fill-color": "#fff8 !important" 
    },
  },
})(TextField);
interface inputTextProps {
  type: string;
  initValue: string;
  value?: string;
  name?: string;
  placeholder?: string;
  ablefunction?: () => void;
  unablefunction?: () => void;
  setName?: (name: string) => void;
  minLength?: number;
  disabled?: boolean;
  onChange: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
}

const InputText: React.FC<inputTextProps> = ({
  type,
  value,
  onChange,
  disabled,
  initValue,
  minLength,
  placeholder,
  name,
}) => {
  const classes = useStyles();

  return (
    <ThemeProvider theme={colortheme}>
      <FormControl
        fullWidth
        sx={{ m: 1 }}
        variant="standard"
        className={classes.inputText}
      >
        <WhiteTextField
          id="input-with-icon-textfield"
          placeholder={placeholder || initValue}
          onChange={e => onChange(e)}
          defaultValue={value}
          name={name || type}
          type={type}
          disabled={disabled}
          required={true}
          sx={{ input: { color: '#c1c1c1', margin: 'unset' } }}
          inputProps={{ minLength }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" style={{ paddingRight: '10px' }}>
                {type === 'name' ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17.889"
                    height="20"
                    viewBox="0 0 17.889 20"
                  >
                    <g
                      id="Icon_feather-user"
                      data-name="Icon feather-user"
                      transform="translate(0.5 0.5)"
                    >
                      <path
                        id="Path_462"
                        data-name="Path 462"
                        d="M22.889,28.833V26.722A4.222,4.222,0,0,0,18.667,22.5H10.222A4.222,4.222,0,0,0,6,26.722v2.111"
                        transform="translate(-6 -9.833)"
                        fill="none"
                        stroke="#c1c1c1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                      />
                      <path
                        id="Path_463"
                        data-name="Path 463"
                        d="M20.444,8.722A4.222,4.222,0,1,1,16.222,4.5a4.222,4.222,0,0,1,4.222,4.222Z"
                        transform="translate(-7.778 -4.5)"
                        fill="none"
                        stroke="#c1c1c1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                      />
                    </g>
                  </svg>
                ) : type === 'date' ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16.414"
                    height="18.127"
                    viewBox="0 0 16.414 18.127"
                  >
                    <g
                      id="Icon_feather-calendar"
                      data-name="Icon feather-calendar"
                      transform="translate(-4 -2.5)"
                    >
                      <path
                        id="Path_464"
                        data-name="Path 464"
                        d="M6.213,6H18.2a1.713,1.713,0,0,1,1.713,1.713V19.7A1.713,1.713,0,0,1,18.2,21.414H6.213A1.713,1.713,0,0,1,4.5,19.7V7.713A1.713,1.713,0,0,1,6.213,6Z"
                        transform="translate(0 -1.287)"
                        fill="none"
                        stroke="#c1c1c1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                      />
                      <path
                        id="Path_465"
                        data-name="Path 465"
                        d="M24,3V6.425"
                        transform="translate(-8.368 0)"
                        fill="none"
                        stroke="#c1c1c1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                      />
                      <path
                        id="Path_466"
                        data-name="Path 466"
                        d="M12,3V6.425"
                        transform="translate(-3.218 0)"
                        fill="none"
                        stroke="#c1c1c1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                      />
                      <path
                        id="Path_467"
                        data-name="Path 467"
                        d="M4.5,15H19.914"
                        transform="translate(0 -5.149)"
                        fill="none"
                        stroke="#c1c1c1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                      />
                    </g>
                  </svg>
                ) : type === 'phone' ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17.914"
                    height="17.948"
                    viewBox="0 0 17.914 17.948"
                  >
                    <path
                      id="Icon_feather-phone"
                      data-name="Icon feather-phone"
                      d="M20.08,15.687v2.551a1.7,1.7,0,0,1-1.854,1.7,16.829,16.829,0,0,1-7.339-2.611,16.582,16.582,0,0,1-5.1-5.1A16.829,16.829,0,0,1,3.175,4.854,1.7,1.7,0,0,1,4.867,3H7.418a1.7,1.7,0,0,1,1.7,1.463,10.919,10.919,0,0,0,.6,2.39,1.7,1.7,0,0,1-.383,1.794l-1.08,1.08a13.606,13.606,0,0,0,5.1,5.1l1.08-1.08a1.7,1.7,0,0,1,1.794-.383,10.919,10.919,0,0,0,2.39.6,1.7,1.7,0,0,1,1.463,1.726Z"
                      transform="translate(-2.667 -2.5)"
                      fill="none"
                      stroke="#c1c1c1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1"
                    />
                  </svg>
                ) : type === 'address' ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15.827"
                    height="19.121"
                    viewBox="0 0 15.827 19.121"
                  >
                    <g
                      id="Icon_feather-map-pin"
                      data-name="Icon feather-map-pin"
                      transform="translate(-4 -1)"
                    >
                      <path
                        id="Path_468"
                        data-name="Path 468"
                        d="M19.327,8.913c0,5.766-7.413,10.708-7.413,10.708S4.5,14.679,4.5,8.913a7.413,7.413,0,0,1,14.827,0Z"
                        transform="translate(0 0)"
                        fill="none"
                        stroke="#c1c1c1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                      />
                      <path
                        id="Path_469"
                        data-name="Path 469"
                        d="M18.442,12.971A2.471,2.471,0,1,1,15.971,10.5,2.471,2.471,0,0,1,18.442,12.971Z"
                        transform="translate(-4.058 -4.058)"
                        fill="none"
                        stroke="#c1c1c1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                      />
                    </g>
                  </svg>
                ) : type === 'city' ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19.7"
                    height="18"
                    viewBox="0 0 19.7 18"
                  >
                    <g
                      id="Icon_feather-map"
                      data-name="Icon feather-map"
                      transform="translate(-1 -2.5)"
                    >
                      <path
                        id="Path_474"
                        data-name="Path 474"
                        d="M1.5,6.4V20l5.95-3.4,6.8,3.4,5.95-3.4V3L14.25,6.4,7.45,3Z"
                        fill="none"
                        stroke="#c1c1c1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                      />
                      <path
                        id="Path_475"
                        data-name="Path 475"
                        d="M12,3V16.6"
                        transform="translate(-4.55)"
                        fill="none"
                        stroke="#c1c1c1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                      />
                      <path
                        id="Path_476"
                        data-name="Path 476"
                        d="M24,9V22.6"
                        transform="translate(-9.75 -2.6)"
                        fill="none"
                        stroke="#c1c1c1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1"
                      />
                    </g>
                  </svg>
                ) : type === 'mail' ? (
                  <MailOutline sx={{ color: '#c1c1c1' }} />
                ) : type === 'user' ? (
                  <PersonOutline sx={{ color: '#c1c1c1' }} />
                ) : type === 'pass' ? (
                  <LockOutlined sx={{ color: '#c1c1c1' }} />
                ) : type === 'cpass' ? (
                  <LockOutlined sx={{ color: '#c1c1c1' }} />
                ) : type === 'bookmark' ? (
                  <BookmarkBorderOutlined sx={{ color: '#c1c1c1' }} />
                ) : type === 'Launch' ? (
                  <LaunchOutlined sx={{ color: '#c1c1c1' }} />
                ) : (
                  <Grid3x3 sx={{ color: '#c1c1c1' }} />
                )}
              </InputAdornment>
            ),
          }}
          variant="standard"
          className={'inputBid'}
        />
      </FormControl>
    </ThemeProvider>
  );
};

export default InputText;
